/**
 * /integrations/home-assistant — Public HA integration guide.
 *
 * Positions TurfPulse as the agronomic brain layer that drops into HA's
 * dashboard via REST sensor — NOT a competitor to Home Assistant. The
 * page answers the only three questions a HA user asks:
 *   1. "What does it cost me to add this?"  (5 min, paste-in YAML)
 *   2. "What do I get back?"                (real fields, real math)
 *   3. "Can I trust the data?"              (citations + open methodology)
 *
 * SEO: indexable, sitemap priority 0.8 (high-intent technical audience).
 * BreadcrumbJsonLd + FaqJsonLd for rich snippet eligibility.
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { SEO } from '../components/SEO';
import { BreadcrumbJsonLd } from '../components/BreadcrumbJsonLd';
import { FaqJsonLd } from '../components/FaqJsonLd';
import { InnerCircleCTA } from '../components/InnerCircleCTA';
import {
  CheckCircle,
  Code,
  Plug,
  Drop,
  CloudSun,
  Bell,
  ArrowRight,
  Lightning,
  ShieldCheck,
} from '@phosphor-icons/react';

const sharedStrengths = [
  'Open-source-friendly philosophy — your data stays portable',
  'Both believe homeowners deserve transparency in automation logic',
  'Both work with bring-your-own hardware (Ecowitt, Rachio, ESPHome)',
  'Both let you script around the rules instead of being trapped by them',
];

const whatYouGet = [
  {
    icon: <Drop size={22} weight="duotone" />,
    title: 'state — single-word call',
    body: '"fire" / "hold" / "spray" / "monitor" / "ok". Drop straight into a `value_template` for an HA sensor. Use it to flash a light, push a notification, or color a Lovelace tile.',
  },
  {
    icon: <CloudSun size={22} weight="duotone" />,
    title: 'mad_pct + mad_status',
    body: 'Management Allowed Depletion as a real number. Trigger an automation when MAD ≥ 50 % (cool-season trigger) or 60 % (warm-season). FAO-56 PAW = 1.5″.',
  },
  {
    icon: <Lightning size={22} weight="duotone" />,
    title: 'spray_status + dewpoint_band',
    body: '"spray" / "marginal" / "no_spray" plus the dewpoint band (minimal/low/moderate/heavy/stuck). Open Δ-T-aware spray windows and disease leaf-wetness alerts in one payload.',
  },
  {
    icon: <Bell size={22} weight="duotone" />,
    title: 'next_run_minutes',
    body: 'How many minutes your next scheduled Rachio fire will run after Smart Split, MAD bypass, dewpoint lock, freeze, wind, and rain skips. The honest, not-the-typed number.',
  },
];

const yamlSensor = `# configuration.yaml
sensor:
  - platform: rest
    name: TurfPulse Today's Call
    resource: https://getturfpulse.com/api/widget/snapshot
    headers:
      Authorization: !secret turfpulse_token
    scan_interval: 300        # 5 min — matches TurfPulse's server cache TTL
    value_template: "{{ value_json.state }}"
    json_attributes:
      - summary
      - ha
    json_attributes_path: "$"`;

const yamlAutomation = `# automations.yaml — example: flash kitchen light when MAD breaches
automation:
  - alias: TurfPulse · MAD breach → kitchen light
    trigger:
      - platform: state
        entity_id: sensor.turfpulse_todays_call
        to: "fire"
    action:
      - service: light.turn_on
        target:
          entity_id: light.kitchen
        data:
          color_name: red
          flash: long
      - service: notify.mobile_app
        data:
          title: "{{ states('sensor.turfpulse_todays_call') | upper }} — TurfPulse"
          message: "{{ state_attr('sensor.turfpulse_todays_call', 'summary') }}"`;

const yamlPanel = `# configuration.yaml — embed the kiosk on your Lovelace sidebar
panel_iframe:
  turfpulse:
    title: "TurfPulse"
    icon: mdi:sprout
    url: "https://getturfpulse.com/widget"
    require_admin: false`;

const setupSteps = [
  'Sign into TurfPulse → Profile → "Generate new token". Copy the `tp_live_…` value (you only see it once).',
  'In Home Assistant, edit `secrets.yaml` and add: `turfpulse_token: "Bearer tp_live_…"`',
  'Paste the REST sensor block into `configuration.yaml`.',
  'Restart Home Assistant (or reload "Manually Configured Sensors" from Developer Tools → YAML).',
  'Open Developer Tools → States and verify `sensor.turfpulse_todays_call` is reporting a value.',
  'Wire it into automations or your Lovelace dashboard. That\'s it — 5 minutes.',
];

const useTogether = [
  'TurfPulse handles every agronomy decision — GDD, MAD, FAO-56 ET₀, Smith-Kerns, dewpoint disease, Δ-T, Bermuda drought floor.',
  'Home Assistant handles your house — lights, locks, presence, voice, scenes, dashboards.',
  'TurfPulse exposes "today\'s call" as a REST sensor; HA reacts however you want.',
  'Want HA to push a notification when TurfPulse skips a fire because of forecast rain? One automation. Done.',
];

const faqs = [
  {
    q: 'Is this a HACS integration?',
    a: 'Not yet — the REST sensor approach works today with zero install. A first-class HACS custom component is on the roadmap for users who want it discoverable in the HA integrations GUI. The data shape and auth are stable, so anything you build today keeps working when HACS lands.',
  },
  {
    q: 'Read-only or read/write?',
    a: 'Read-only. Personal API tokens cannot fire schedules, change settings, promote founders, or delete anything. The hybrid auth dependency is mounted ONLY on safe read endpoints. If you want to fire a Rachio zone from HA, point HA at Rachio\'s native cloud API directly — TurfPulse never wants to be in the middle of a destructive action it can\'t audit.',
  },
  {
    q: 'How is the auth protected?',
    a: 'Tokens are random 128-bit values, stored at rest as SHA-256 hashes (we never see the raw value after creation). Tokens are scoped to YOUR account only — they can\'t read another user\'s data. Revoke from Profile any time; the next request fails immediately.',
  },
  {
    q: 'What\'s the rate limit?',
    a: 'The widget snapshot endpoint server-side caches per user for 5 min, so HA polling at scan_interval: 300 hits the cache directly. You can poll faster but you\'ll see the same payload. Other endpoints (live ecowitt, calendar) hit upstream APIs — keep them at 5+ min.',
  },
  {
    q: 'Can I run TurfPulse fully offline / on my own HA box?',
    a: 'No — TurfPulse is a hosted SaaS. The agronomy models (Smith-Kerns, FAO-56 ET₀, Bermuda drought floor, GDD) get tuned weekly as we onboard more soil-test feedback. Self-hosting would freeze the math on whatever day you cloned it. If you want fully local irrigation logic, OpenSprinkler + your own scripts is a legitimate path — TurfPulse is for people who want the agronomy curated.',
  },
  {
    q: 'I\'m running ESPHome / Tasmota / a Pi with a custom probe. Will it work?',
    a: 'If your probe data lives in HA, you can pipe it INTO TurfPulse via webhook (roadmap, Phase 2). If your probe is an Ecowitt WH51 / WN34 / WH52, just connect it natively in TurfPulse Profile → Ecowitt and skip the bridge.',
  },
  {
    q: 'Do you have a sample Lovelace card?',
    a: 'Yes — `panel_iframe` works today (config snippet above). A first-party Lovelace custom card is on the roadmap. The REST sensor + a stock entities card already gets you 90 % of the visual.',
  },
];

const HomeAssistantIntegrationPage = () => {
  const breadcrumbs = [
    { label: 'Home', path: '/' },
    { label: 'Integrations', path: '/integrations' },
    { label: 'Home Assistant', path: '/integrations/home-assistant' },
  ];

  return (
    <div className="min-h-screen bg-background" data-testid="ha-integration-page">
      <SEO
        title="Home Assistant Integration · TurfPulse"
        description="Drop TurfPulse's agronomic brain (MAD bucket, dewpoint disease windows, Smart Split irrigation) into Home Assistant in 5 minutes via the REST sensor platform. Read-only personal API tokens, citation-backed data."
        path="/integrations/home-assistant"
      />
      <BreadcrumbJsonLd items={breadcrumbs} />
      <FaqJsonLd faqs={faqs} />

      {/* Hero */}
      <section className="px-4 sm:px-6 py-16 border-b border-border/60">
        <div className="max-w-4xl mx-auto space-y-6">
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            <Plug size={14} weight="duotone" />
            <span>Integration · Read-only · Free with any TurfPulse plan</span>
          </div>
          <h1 className="text-3xl sm:text-5xl font-bold text-foreground leading-tight">
            Home Assistant runs your house.<br />
            <span className="text-primary">TurfPulse runs your lawn.</span>
          </h1>
          <p className="text-base sm:text-lg text-muted-foreground max-w-2xl leading-relaxed">
            We're the agronomic brain HA users have been trying to build with custom Python and ten HACS add-ons. Drop TurfPulse into your dashboard
            in 5 minutes via the REST sensor. Get back: MAD bucket, dewpoint disease windows, Smart Split irrigation, Smith-Kerns dollar spot
            pressure — every datum tied to a citation, every decision auditable.
          </p>
          <div className="flex flex-wrap gap-3 pt-2">
            <Button asChild size="lg" data-testid="ha-cta-token">
              <Link to="/profile">Generate your API token <ArrowRight className="ml-2" size={18} /></Link>
            </Button>
            <Button asChild variant="outline" size="lg" data-testid="ha-cta-methodology">
              <Link to="/methodology/probe-driven-irrigation">See the methodology</Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Shared philosophy */}
      <section className="px-4 sm:px-6 py-14 bg-muted/30">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl sm:text-3xl font-bold text-foreground mb-3">
            What we have in common
          </h2>
          <p className="text-muted-foreground mb-8 max-w-2xl">
            HA users and TurfPulse users tend to be the same person — someone who wants the math to be visible, the data to be portable,
            and the automation to be honest about why it just made a decision.
          </p>
          <ul className="grid sm:grid-cols-2 gap-4">
            {sharedStrengths.map((s) => (
              <li
                key={s}
                className="flex gap-3 items-start p-4 rounded-lg border border-border/60 bg-background"
                data-testid="ha-shared-strength"
              >
                <CheckCircle size={22} weight="duotone" className="text-primary flex-shrink-0 mt-0.5" />
                <span className="text-sm text-foreground leading-relaxed">{s}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* What you get */}
      <section className="px-4 sm:px-6 py-14">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl sm:text-3xl font-bold text-foreground mb-3">
            What lands on your HA dashboard
          </h2>
          <p className="text-muted-foreground mb-8 max-w-2xl">
            One JSON snapshot per 5 min, populated with everything a homeowner-grade weather station + sprinkler controller can't tell you on its own:
          </p>
          <div className="grid sm:grid-cols-2 gap-5">
            {whatYouGet.map((item) => (
              <Card key={item.title} data-testid="ha-feature-card" className="border-primary/30 bg-background">
                <CardContent className="p-6 space-y-3">
                  <div className="text-primary">{item.icon}</div>
                  <h3 className="font-semibold text-foreground text-base leading-tight">{item.title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{item.body}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Setup — copy-paste YAML */}
      <section className="px-4 sm:px-6 py-14 bg-muted/30">
        <div className="max-w-4xl mx-auto space-y-8">
          <div>
            <h2 className="text-2xl sm:text-3xl font-bold text-foreground mb-3 flex items-center gap-2">
              <Code size={26} weight="duotone" className="text-primary" /> 5-minute setup
            </h2>
            <ol className="list-decimal pl-6 space-y-2 text-sm text-foreground/90">
              {setupSteps.map((s, i) => (
                <li key={i} data-testid="ha-setup-step" className="leading-relaxed">{s}</li>
              ))}
            </ol>
          </div>

          <div>
            <h3 className="font-semibold text-foreground mb-2 flex items-center gap-2">
              <Drop size={18} weight="duotone" /> 1. REST sensor — paste into <code>configuration.yaml</code>
            </h3>
            <pre
              className="text-xs sm:text-sm bg-zinc-950 text-emerald-200 rounded-lg p-4 overflow-x-auto leading-relaxed"
              data-testid="ha-yaml-sensor"
            >
{yamlSensor}
            </pre>
          </div>

          <div>
            <h3 className="font-semibold text-foreground mb-2 flex items-center gap-2">
              <Bell size={18} weight="duotone" /> 2. Example automation — kitchen light flashes red on MAD breach
            </h3>
            <pre
              className="text-xs sm:text-sm bg-zinc-950 text-emerald-200 rounded-lg p-4 overflow-x-auto leading-relaxed"
              data-testid="ha-yaml-automation"
            >
{yamlAutomation}
            </pre>
          </div>

          <div>
            <h3 className="font-semibold text-foreground mb-2 flex items-center gap-2">
              <CloudSun size={18} weight="duotone" /> 3. Or embed the kiosk in your sidebar (no YAML for the dashboard)
            </h3>
            <pre
              className="text-xs sm:text-sm bg-zinc-950 text-emerald-200 rounded-lg p-4 overflow-x-auto leading-relaxed"
              data-testid="ha-yaml-panel"
            >
{yamlPanel}
            </pre>
          </div>
        </div>
      </section>

      {/* Use together */}
      <section className="px-4 sm:px-6 py-14">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-start gap-3 mb-4">
            <Plug size={28} weight="duotone" className="text-primary mt-1" />
            <h2 className="text-2xl sm:text-3xl font-bold text-foreground">
              The honest split
            </h2>
          </div>
          <Card className="border-primary/40 bg-primary/5">
            <CardContent className="p-6">
              <ul className="space-y-3">
                {useTogether.map((step, i) => (
                  <li key={i} className="flex gap-3 items-start" data-testid="ha-use-together-step">
                    <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary text-primary-foreground text-xs font-bold flex items-center justify-center">
                      {i + 1}
                    </span>
                    <span className="text-sm text-foreground leading-relaxed pt-0.5">{step}</span>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Trust block */}
      <section className="px-4 sm:px-6 py-12 bg-muted/30 border-y border-border/60">
        <div className="max-w-4xl mx-auto flex items-start gap-4">
          <ShieldCheck size={36} weight="duotone" className="text-emerald-600 mt-1 flex-shrink-0" />
          <div>
            <h3 className="font-semibold text-foreground mb-1">Token security in plain English</h3>
            <p className="text-sm text-muted-foreground leading-relaxed">
              Tokens are 128 bits of entropy, stored at rest as SHA-256 hashes — even if our database leaked tomorrow, no one can read your raw token.
              We show you the raw value <strong>exactly once at creation</strong>, then it's gone from our side. Tokens are scoped to your account
              only and grant <strong>read access</strong> — no destructive actions, no Rachio fire, no payment changes. Revoke any time from{' '}
              <Link to="/profile" className="text-emerald-600 underline">Profile → Personal API Tokens</Link>.
            </p>
          </div>
        </div>
      </section>

      {/* Webhooks — the closed-loop, Inner Circle paid feature */}
      <section className="px-4 sm:px-6 py-14 bg-emerald-500/5 border-y border-emerald-500/20" id="webhooks" data-testid="ha-webhooks-section">
        <div className="max-w-4xl mx-auto space-y-6">
          <div className="flex items-start gap-3">
            <Lightning size={32} weight="duotone" className="text-emerald-600 mt-1" />
            <div>
              <div className="text-xs font-semibold uppercase tracking-wider text-emerald-700 dark:text-emerald-300 mb-1">
                Inner Circle feature · the closed loop
              </div>
              <h2 className="text-2xl sm:text-3xl font-bold text-foreground">
                Don't poll. Get pushed.
              </h2>
            </div>
          </div>
          <p className="text-muted-foreground max-w-2xl leading-relaxed">
            The REST sensor above polls every 5 min. Webhooks push events to your HA box the instant they happen — your kitchen lights flash
            when MAD breaches at 2 AM, your phone notifies when the spray window opens, your dashboard reacts in real time.
            HMAC-SHA256 signed (Stripe pattern, you can verify), HTTPS-required for public URLs, auto-disabled after 10 consecutive failures
            so a forgotten HA reboot never causes a runaway.
          </p>

          <div className="rounded-lg border border-border/60 bg-background overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-muted/40 text-xs uppercase tracking-wider text-muted-foreground">
                <tr><th className="text-left p-3 font-semibold">Event</th><th className="text-left p-3 font-semibold">Fires when</th></tr>
              </thead>
              <tbody className="divide-y divide-border/60">
                <tr><td className="p-3 font-mono text-xs">schedule.fired</td><td className="p-3 text-muted-foreground">Rachio actually ran (post-skip-checks, post-Smart-Split, post-Guardian)</td></tr>
                <tr><td className="p-3 font-mono text-xs">schedule.skipped</td><td className="p-3 text-muted-foreground">Guardian held the fire — rain, freeze, MAD bucket sufficient, dewpoint locked</td></tr>
                <tr><td className="p-3 font-mono text-xs">mad.breached</td><td className="p-3 text-muted-foreground">DRY zone hit the TAMU 12% drought floor — irrigate-now signal</td></tr>
                <tr><td className="p-3 font-mono text-xs">dewpoint.locked</td><td className="p-3 text-muted-foreground">Overnight dewpoint stuck above 65°F — disease pressure window active</td></tr>
                <tr><td className="p-3 font-mono text-xs">spray.window.open</td><td className="p-3 text-muted-foreground">Δ-T + wind + temp aligned for safe spray application</td></tr>
              </tbody>
            </table>
          </div>

          <div>
            <h3 className="font-semibold text-foreground mb-2 flex items-center gap-2">
              <Code size={18} weight="duotone" /> Receiving end — paste into HA <code>configuration.yaml</code>
            </h3>
            <pre className="text-xs sm:text-sm bg-zinc-950 text-emerald-200 rounded-lg p-4 overflow-x-auto leading-relaxed" data-testid="ha-yaml-webhook">
{`# configuration.yaml — receive a TurfPulse webhook
automation:
  - alias: TurfPulse · MAD breach received
    trigger:
      - platform: webhook
        webhook_id: turfpulse_events
        local_only: false
    condition:
      - "{{ trigger.json.event == 'mad.breached' }}"
    action:
      - service: light.turn_on
        target:
          entity_id: light.kitchen
        data:
          color_name: red
          flash: long
      - service: notify.mobile_app
        data:
          title: "MAD breach"
          message: "{{ trigger.json.data.snapshot.mad_pct }}% — irrigate"

# Then in TurfPulse Profile → Outbound Webhooks, register:
#   URL: https://YOUR-HA.example.com/api/webhook/turfpulse_events
#   Events: mad.breached, schedule.fired, schedule.skipped`}
            </pre>
          </div>

          <div>
            <h3 className="font-semibold text-foreground mb-2 flex items-center gap-2">
              <ShieldCheck size={18} weight="duotone" /> Verifying the HMAC signature (optional, but recommended)
            </h3>
            <pre className="text-xs sm:text-sm bg-zinc-950 text-emerald-200 rounded-lg p-4 overflow-x-auto leading-relaxed" data-testid="ha-yaml-hmac">
{`# Header that lands on every TurfPulse webhook delivery:
#   X-TurfPulse-Signature: t=1730000000,v1=<hex_sha256>
#
# Verify by recomputing HMAC-SHA256 over "<t>.<raw_body>"
# with your whsec_ secret. Reject if they don't match.
#
# Python example:
import hmac, hashlib
expected = hmac.new(secret.encode(), f"{t}.".encode() + body, hashlib.sha256).hexdigest()
assert hmac.compare_digest(expected, sig)`}
            </pre>
          </div>

          <div className="flex items-center gap-3 pt-2">
            <InnerCircleCTA testId="ha-webhooks-cta" size="default" />
          </div>
        </div>
      </section>

      {/* The honest "why not DIY?" objection handler */}
      <section className="px-4 sm:px-6 py-14" data-testid="ha-diy-section">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl sm:text-3xl font-bold text-foreground mb-3">
            "Why not just build this in HA myself?"
          </h2>
          <p className="text-muted-foreground mb-6 max-w-2xl">
            Fair question — and a fair fight. You can. We've published the entire methodology open at{' '}
            <Link to="/methodology/probe-driven-irrigation" className="text-emerald-600 underline">/methodology/probe-driven-irrigation</Link>.
            If you've got 40+ hours and want to read the source papers yourself, here's exactly what TurfPulse productizes for you:
          </p>

          <div className="rounded-lg border border-border/60 bg-background overflow-hidden mb-6">
            <table className="w-full text-sm">
              <thead className="bg-muted/40 text-xs uppercase tracking-wider text-muted-foreground">
                <tr>
                  <th className="text-left p-3 font-semibold">Component</th>
                  <th className="text-left p-3 font-semibold">Source</th>
                  <th className="text-right p-3 font-semibold">DIY hours</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border/60">
                <tr data-testid="diy-row">
                  <td className="p-3 font-medium">FAO-56 ET₀ + Penman-Monteith + Kc</td>
                  <td className="p-3 text-muted-foreground">UN FAO Irrigation &amp; Drainage Paper 56 (1998, ~200 pp)</td>
                  <td className="p-3 text-right tabular-nums">15–20</td>
                </tr>
                <tr data-testid="diy-row">
                  <td className="p-3 font-medium">Smith-Kerns dollar spot regression</td>
                  <td className="p-3 text-muted-foreground">Smith-Kerns 2013 PLoS ONE (peer-reviewed)</td>
                  <td className="p-3 text-right tabular-nums">6–10</td>
                </tr>
                <tr data-testid="diy-row">
                  <td className="p-3 font-medium">MAD bucket + per-soil PAW</td>
                  <td className="p-3 text-muted-foreground">USDA NRCS soil texture triangle</td>
                  <td className="p-3 text-right tabular-nums">5–8</td>
                </tr>
                <tr data-testid="diy-row">
                  <td className="p-3 font-medium">Smart Split (gap-weighted multi-day)</td>
                  <td className="p-3 text-muted-foreground">Bermuda root-depth research (TAMU AgriLife)</td>
                  <td className="p-3 text-right tabular-nums">8–12</td>
                </tr>
                <tr data-testid="diy-row">
                  <td className="p-3 font-medium">Bermuda 12 % SVWC drought floor</td>
                  <td className="p-3 text-muted-foreground">Wherley et al. fine sandy loam fairway research</td>
                  <td className="p-3 text-right tabular-nums">3–5</td>
                </tr>
                <tr data-testid="diy-row">
                  <td className="p-3 font-medium">Δ-T wet-bulb spray window</td>
                  <td className="p-3 text-muted-foreground">Stull 2011 wet-bulb approximation (Aus/UK canonical)</td>
                  <td className="p-3 text-right tabular-nums">2–3</td>
                </tr>
                <tr data-testid="diy-row">
                  <td className="p-3 font-medium">Dewpoint disease bands + GDD</td>
                  <td className="p-3 text-muted-foreground">Multiple turf-extension references</td>
                  <td className="p-3 text-right tabular-nums">5–8</td>
                </tr>
                <tr data-testid="diy-row">
                  <td className="p-3 font-medium">FRAC / HRAC / IRAC rotation tracker</td>
                  <td className="p-3 text-muted-foreground">FRAC 2026 code list (annual update)</td>
                  <td className="p-3 text-right tabular-nums">4–6</td>
                </tr>
                <tr className="bg-emerald-500/5 border-t-2 border-emerald-500/30">
                  <td className="p-3 font-semibold">Total + ongoing maintenance</td>
                  <td className="p-3 text-muted-foreground italic">All of the above, kept tuned as research updates</td>
                  <td className="p-3 text-right tabular-nums font-bold text-emerald-700 dark:text-emerald-300">
                    48–72 hrs<br /><span className="text-xs font-normal">+ indefinite</span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <Card className="border-border/60 bg-background">
              <CardContent className="p-5 space-y-2">
                <h3 className="font-semibold text-foreground text-sm">If you want to build it</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  We'd actually love to read your write-up. Open the methodology page, fork the math, ship it as a HACS package — you'll
                  learn a ton and the HA community will benefit. Send us a link, we'll cross-link it from this page.
                </p>
                <Button asChild variant="outline" size="sm" data-testid="ha-diy-methodology-cta">
                  <Link to="/methodology/probe-driven-irrigation">Read the methodology →</Link>
                </Button>
              </CardContent>
            </Card>
            <Card className="border-primary/40 bg-primary/5">
              <CardContent className="p-5 space-y-2">
                <h3 className="font-semibold text-foreground text-sm">If you'd rather skip to the part where your grass looks better</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  $80/year (Enthusiast Annual). All of the above, tuned weekly as we onboard more soil-test feedback. 5-min HA setup.
                  Cancel any time. Free tier covers most homeowners — paid only if you want the closed-loop webhooks + 365-day depletion history.
                </p>
                <Button asChild size="sm" data-testid="ha-diy-upgrade-cta">
                  <Link to="/?auth=1">See plans →</Link>
                </Button>
              </CardContent>
            </Card>
          </div>

          <p className="text-xs text-muted-foreground mt-6 italic max-w-2xl">
            Honest disclosure: a few HA tinkerers will rebuild this from the methodology and never pay. That's fine — they're not our customer
            and we're a fan of open agronomy. The other 95 % of HA users would rather skip 60 hours of FAO-56 reading. That's who we built TurfPulse for.
          </p>
        </div>
      </section>

      {/* FAQ */}
      <section className="px-4 sm:px-6 py-14">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl sm:text-3xl font-bold text-foreground mb-8">Common questions</h2>
          <div className="space-y-5">
            {faqs.map((f) => (
              <div key={f.q} className="border-b border-border/60 pb-5 last:border-0" data-testid="ha-faq">
                <h3 className="font-semibold text-foreground mb-2">{f.q}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{f.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="px-4 sm:px-6 py-16 border-t border-border/60">
        <div className="max-w-3xl mx-auto text-center space-y-5">
          <h2 className="text-2xl sm:text-3xl font-bold text-foreground">
            Ready in 5 minutes. Free with any plan.
          </h2>
          <p className="text-muted-foreground max-w-xl mx-auto">
            Generate a token, paste the YAML, restart HA. If it doesn't land in your Lovelace dashboard within the next coffee break, email us — we read every word.
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            <Button asChild size="lg" data-testid="ha-footer-cta-token">
              <Link to="/profile">Generate token <ArrowRight className="ml-2" size={18} /></Link>
            </Button>
            <Button asChild variant="outline" size="lg" data-testid="ha-footer-cta-hey">
              <Link to="/hey">Try TurfPulse first (no signup)</Link>
            </Button>
          </div>

          {/* Inner Circle upsell — for HA users who want webhooks + 365-day history */}
          <div className="pt-8 mt-8 border-t border-border/60 space-y-4">
            <div className="text-xs font-semibold uppercase tracking-wider text-emerald-700 dark:text-emerald-300">
              Or claim a founding seat
            </div>
            <p className="text-sm text-muted-foreground max-w-xl mx-auto">
              Inner Circle members get outbound webhooks (HA reacts in real time, not on 5-min polls), 365-day depletion history, 10 API tokens,
              and the founding-member crest. <strong>Free through Oct 31, 2026 · then $4.99/mo locked for life.</strong> Card captured at signup, $0 charged today.
            </p>
            <div className="flex justify-center">
              <InnerCircleCTA testId="ha-inner-circle-cta" />
            </div>
          </div>

          <p className="text-xs text-muted-foreground pt-4 max-w-lg mx-auto">
            "Home Assistant" is a trademark of the Open Home Foundation. This page reflects the TurfPulse team's
            integration approach and is not affiliated with or endorsed by Home Assistant or the Open Home Foundation.
          </p>
        </div>
      </section>
    </div>
  );
};

export default HomeAssistantIntegrationPage;
